import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, FileText, ExternalLink, Lock } from "lucide-react";
import { adminListPosts, adminDeletePost, formatApiError } from "../../lib/adminApi";
import {
    chipClasses, dotClasses, formatDate, getCategory,
} from "../../lib/updates";

export default function AdminPostsList() {
    const [rows, setRows] = useState(null);

    const refresh = () => {
        adminListPosts()
            .then(setRows)
            .catch(() => setRows([]));
    };
    useEffect(() => { refresh(); }, []);

    const handleDelete = async (slug) => {
        if (!window.confirm(`Slet indlægget "${slug}"? Dette kan ikke fortrydes.`)) return;
        try {
            await adminDeletePost(slug);
            toast.success("Indlæg slettet");
            refresh();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <div data-testid="admin-posts-page">
            <div className="flex items-end justify-between gap-4">
                <div>
                    <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">
                        Updates / Indlæg
                    </h1>
                    <p className="mt-1 text-slate-600">
                        Markdown-filer fra koden (låst) + posts oprettet via dette panel.
                    </p>
                </div>
                <Link
                    to="/admin/posts/new"
                    data-testid="admin-posts-new"
                    className="inline-flex items-center gap-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-semibold px-4 h-10 text-sm transition-colors"
                >
                    <Plus className="h-4 w-4" />
                    Nyt indlæg
                </Link>
            </div>

            <div className="mt-6 rounded-2xl bg-white border border-slate-200 overflow-hidden">
                {rows === null ? (
                    <div className="p-10 text-center text-slate-500">Henter…</div>
                ) : rows.length === 0 ? (
                    <div className="p-12 text-center text-slate-500">
                        <FileText className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                        <p>Ingen indlæg endnu. Tryk "Nyt indlæg" for at oprette det første.</p>
                    </div>
                ) : (
                    <table className="min-w-full text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200">
                            <tr>
                                <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wider">Titel</th>
                                <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wider">Kategori</th>
                                <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wider">Dato</th>
                                <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wider">Status</th>
                                <th className="text-right px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wider">Handlinger</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((p) => {
                                const cat = getCategory(p.category);
                                const isFile = p.source === "file";
                                return (
                                    <tr key={p.slug} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50" data-testid={`admin-post-row-${p.slug}`}>
                                        <td className="px-4 py-3 align-top">
                                            <div className="font-semibold text-slate-900">{p.title}</div>
                                            <code className="text-[10px] text-slate-500">{p.slug}</code>
                                        </td>
                                        <td className="px-4 py-3 align-top">
                                            <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${chipClasses(cat.color)}`}>
                                                <span className={`h-1.5 w-1.5 rounded-full ${dotClasses(cat.color)}`} />
                                                {cat.label}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 align-top whitespace-nowrap text-slate-600">{formatDate(p.date)}</td>
                                        <td className="px-4 py-3 align-top">
                                            {isFile ? (
                                                <span className="inline-flex items-center gap-1 text-[11px] text-slate-500" title="Markdown-fil — kan kun redigeres i koden">
                                                    <Lock className="h-3 w-3" />
                                                    Fil
                                                </span>
                                            ) : p.published ? (
                                                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700">
                                                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                                                    Publiceret
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700">
                                                    <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                                                    Kladde
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 align-top text-right">
                                            <div className="inline-flex items-center gap-1">
                                                <a
                                                    href={`/updates/${p.slug}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    title="Se på sitet"
                                                    className="h-8 w-8 rounded-md hover:bg-slate-100 inline-flex items-center justify-center text-slate-500"
                                                >
                                                    <ExternalLink className="h-4 w-4" />
                                                </a>
                                                {!isFile && (
                                                    <>
                                                        <Link
                                                            to={`/admin/posts/${p.slug}`}
                                                            title="Rediger"
                                                            data-testid={`admin-post-edit-${p.slug}`}
                                                            className="h-8 w-8 rounded-md hover:bg-slate-100 inline-flex items-center justify-center text-slate-500 hover:text-sky-700"
                                                        >
                                                            <Edit3 className="h-4 w-4" />
                                                        </Link>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleDelete(p.slug)}
                                                            title="Slet"
                                                            data-testid={`admin-post-delete-${p.slug}`}
                                                            className="h-8 w-8 rounded-md hover:bg-rose-50 inline-flex items-center justify-center text-slate-500 hover:text-rose-600"
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
